import { environment } from "@app/relay/relay-environment";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { commitMutation, graphql } from "react-relay";

import type {
  BasisEnum,
  generateExportButtonMutation,
} from "@app/queries/generateExportButtonMutation.graphql";

const mutation = graphql`
  mutation generateExportButtonMutation(
    $kind: String!
    $perspectiveId: ID
    $basis: BasisEnum
    $from: String
  ) {
    exportCreate(
      kind: $kind
      perspectiveId: $perspectiveId
      basis: $basis
      from: $from
    ) {
      export {
        id
        status
      }
      errors {
        message
      }
    }
  }
`;

// Queues a report and sends the user to /exports to watch it.
//
// These used to be synchronous downloads: the board pack and the cohort matrix
// were derived inside the web request and streamed to the browser, which is
// minutes of work on a request thread and left no record once the tab closed.
// The button now starts a run and navigates; the file arrives in the list.
export default function GenerateExportButton({
  kind,
  label,
  from,
  perspectiveId,
  basis,
  className,
  onQueued,
}: {
  kind: string;
  label: string;
  from?: string | null;
  perspectiveId?: string | null;
  basis?: BasisEnum | null;
  className?: string;
  // Lets a caller navigate somewhere other than /exports (the P&L sends people
  // to its own report page). Suppresses the default navigation entirely.
  onQueued?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const navigate = useNavigate();

  const start = () => {
    setBusy(true);
    setNotice(null);
    commitMutation<generateExportButtonMutation>(environment, {
      mutation,
      variables: {
        kind,
        from: from ?? null,
        perspectiveId: perspectiveId ?? null,
        basis: basis ?? null,
      },
      onCompleted: (response, errors) => {
        setBusy(false);
        const failure = (errors ?? response.exportCreate?.errors)?.[0]?.message;
        if (failure) {
          setNotice(failure);
          return;
        }
        if (onQueued) onQueued();
        else navigate({ to: "/exports" });
      },
      onError: () => {
        setBusy(false);
        setNotice("Could not start the export. Please try again.");
      },
    });
  };

  return (
    <div className="flex items-center gap-3">
      {notice && <span className="text-xs text-red-600">{notice}</span>}
      <button
        type="button"
        disabled={busy}
        onClick={start}
        title="Generates in the background; the file appears under Exports"
        className={
          className ??
          "rounded border px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50"
        }
      >
        {busy ? "Starting…" : label}
      </button>
    </div>
  );
}
